import type { HTMLAttributes} from 'react';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { Flame } from '@zoomit/icons';
import { Image, Link } from '@zoomit/primitives';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useOrigin } from '../../providers/OriginProvider';
import type { ArticleCardResponse } from '../../types/articles';
import BrowseArticleListItemMobileActionSection from './BrowseArticleListItemMobileActionSection';

type OptionsType = {
  handleEvent?: (clickPosition: string) => void;
};

type Props = ArticleCardResponse & OptionsType & HTMLAttributes<HTMLAnchorElement>;

const BrowseArticleListItemMobile = (props: Props) => {
  const { title, coverImageLink, linkIsFollow, type, isFeatured = false, handleEvent, ...rest } = props;
  const { baseUrl, baseMediaUrl } = useOrigin();

  const linkAttr = {
    rel: type === 'External' ? (linkIsFollow ? undefined : 'nofollow') : undefined,
    target: type === 'External' ? '_blank' : undefined,
    href: type === 'External' ? props.link : `${baseUrl}/${props.slug}/`,
    prefetch: type === 'External' ? false : true,
  };

  if (isFeatured) {
    return (
      <Link {...linkAttr} {...rest}>
        <Image
          className="aspect-primary h-auto w-full rounded-sm object-cover"
          width={328}
          height={184.5}
          placeholder="blur"
          alt={coverImageLink.alt}
          blurDataURL={coverImageLink.preview}
          src={`${baseMediaUrl}/${coverImageLink.fileName}-${coverImageLink.id}`}
          sizes="384px"
        />
        <div className="flex flex-nowrap items-start gap-2">
          {type === 'Internal' && props.isTrend && (
            <div className="mt-3 shrink-0">
              <Flame fill={cssVar(theme.primary1)} />
            </div>
          )}
          <Typography
            style={{ display: 'inline-block' }}
            fontWeight="bold"
            fontSize={1.4}
            lineHeight={2}
            spacing={{ mt: 'x2' }}
          >
            {title}
          </Typography>
        </div>
        <BrowseArticleListItemMobileActionSection {...props} />
      </Link>
    );
  }

  return (
    <Link {...linkAttr} {...rest}>
      <div className="flex flex-nowrap justify-between gap-4">
        <div className="flex max-w-[calc(100%-80px)] flex-nowrap items-start gap-2">
          {type === 'Internal' && props.isTrend && (
            <Flame style={{ flexShrink: 0 }} size="dl" fill={cssVar(theme.primary1)} />
          )}
          <Typography
            className="color-exception-title"
            fontWeight="bold"
            fontSize={1.4}
            lineHeight={2}
            onClick={() => handleEvent?.('title')}
          >
            {title}
          </Typography>
        </div>
        <Image
          className="h-[104px] rounded-sm"
          onClick={() => handleEvent?.('image')}
          width={104}
          height={104}
          alt={coverImageLink.alt}
          style={{ objectFit: 'cover' }}
          src={`${baseMediaUrl}/${coverImageLink.fileName}-${coverImageLink.id}`}
          blurDataURL={coverImageLink.preview}
          placeholder="blur"
          sizes="160px"
        />
      </div>
      <BrowseArticleListItemMobileActionSection {...props} />
    </Link>
  );
};

export default memo(BrowseArticleListItemMobile, isEqual);
